var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { useStorage } from '../../utils/hooks/useStorage';
const DISMISSED_ALERTS_STORAGE_KEY = 'DISSMISSED_ALERTS_STORAGE_KEY';
export var ALERT_TYPE;
(function (ALERT_TYPE) {
    ALERT_TYPE["INFORMATIONAL"] = "Informational";
    ALERT_TYPE["INFORMATIONAL_YELLOW"] = "Informational_Yellow";
    ALERT_TYPE["WARNING"] = "Warning";
    ALERT_TYPE["SUCCESS"] = "Success";
})(ALERT_TYPE || (ALERT_TYPE = {}));
export var ALERT_DOMAIN;
(function (ALERT_DOMAIN) {
    ALERT_DOMAIN["LIBRARY"] = "library";
    ALERT_DOMAIN["PRIMO"] = "primo";
    ALERT_DOMAIN["PRIMO_NDU"] = "primoNDU";
    ALERT_DOMAIN["PRIMO_HCC"] = "primoHCC";
    ALERT_DOMAIN["ILLIAD"] = "illiad";
    ALERT_DOMAIN["CATALOGUE_CLASSIC_NDU"] = "catalogueClassicNDU";
    ALERT_DOMAIN["CATALOGUE_CLASSIC_HCC"] = "catalogueClassicHCC";
})(ALERT_DOMAIN || (ALERT_DOMAIN = {}));
const composeGraphqlQuery = ({ startTime, endTime, }) => {
    return JSON.stringify({
        query: `
      query AlertQuery($startTime: DateTime, $endTime: DateTime) {
        alerts(
          filters: {
            startTime: { lte: $startTime }
            endTime: { gte: $endTime }
          }
          pagination: { start: 0, limit: 100 }
          sort: ["type:desc", "startTime:asc"]
        ) {
          data {
            id
            attributes {
              title
              description
              type
              global
              domains
            }
          }
        }
      }
    `,
        variables: {
            startTime,
            endTime,
        },
    });
};
const DEFAULT_DATE = new Date().toISOString();
const useAlertsQuery = ({ domain = ALERT_DOMAIN.LIBRARY, endpoint = DEFAULT_ENDPOINT, startTime = DEFAULT_DATE, endTime = DEFAULT_DATE, disable = false, }) => {
    const [alerts, setAlerts] = useState([]);
    useEffect(() => {
        const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                const response = yield fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: composeGraphqlQuery({ startTime, endTime }),
                });
                const json = yield response.json();
                if ((_b = (_a = json === null || json === void 0 ? void 0 : json.data) === null || _a === void 0 ? void 0 : _a.alerts) === null || _b === void 0 ? void 0 : _b.data) {
                    const results = json.data.alerts.data.map((a) => (Object.assign({ id: a.id }, a.attributes)));
                    setAlerts(results);
                }
            }
            catch (error) {
                console.error('error', error);
            }
        });
        if (!disable) {
            fetchData();
        }
    }, [endpoint, domain, startTime, endTime, disable]);
    return alerts;
};
const AlertsContext = React.createContext({
    alerts: [],
    dismiss: () => { },
});
const DEFAULT_ENDPOINT = 'https://library-website-cms.library.nd.edu/graphql';
export const AlertsProvider = ({ domain = ALERT_DOMAIN.LIBRARY, endpoint, startTime, endTime, children, disable, }) => {
    const alerts = useAlertsQuery({
        domain,
        endpoint,
        startTime,
        endTime,
        disable,
    });
    const storage = useStorage(`alert-storage-${domain}`);
    const storedDismissedAlerts = storage.getItem(DISMISSED_ALERTS_STORAGE_KEY);
    const [dismissedAlerts, setDismissedAlerts] = useState(storedDismissedAlerts ? JSON.parse(storedDismissedAlerts) : []);
    const dismiss = (id) => {
        if (!dismissedAlerts.includes(id)) {
            const newDismissed = [...dismissedAlerts, id];
            storage.setItem(DISMISSED_ALERTS_STORAGE_KEY, JSON.stringify(newDismissed));
            setDismissedAlerts(newDismissed);
        }
    };
    const filteredAlerts = alerts
        .filter((alert) => !dismissedAlerts.includes(alert.id))
        .filter((alert) => { var _a; return (_a = alert === null || alert === void 0 ? void 0 : alert.domains) === null || _a === void 0 ? void 0 : _a.includes(domain); });
    return (_jsx(AlertsContext.Provider, Object.assign({ value: { alerts: filteredAlerts, dismiss } }, { children: children })));
};
export const useAlerts = (pageAlerts = []) => {
    const { alerts: allAlerts, dismiss } = React.useContext(AlertsContext);
    const alerts = pageAlerts.length > 0
        ? allAlerts.filter((alert) => alert.global || pageAlerts.includes(alert.id))
        : allAlerts.filter((alert) => alert.global);
    return {
        alerts,
        dismiss,
    };
};
